import React, { MouseEvent, ReactElement, useEffect } from "react";
import Confetti from "react-confetti";
import { BiBookHeart, BiHelpCircle } from "react-icons/bi";
import {
    BsFillMegaphoneFill,
    BsGearFill,
    BsHandThumbsUp, BsList, BsShopWindow, BsShuffle,
} from "react-icons/bs";
import { FaInbox, FaUsers } from "react-icons/fa";
import { IoPerson, IoTimeOutline } from "react-icons/io5";
import { AiOutlinePlus } from "react-icons/ai";
import Help, { ARTICLES } from "../../components/help/Help";
import SquareIcon from "../../components/square-icon/SquareIcon";
import useRouting, { NavLink, Paths } from "../../contexts/Routing";
import { Permission, useGetOnboardingProfileQuery, useSaveOnboardingProfileMutation } from "../../__generated__/api";
import useCurrentSession from "../../contexts/session";
import { removeTypename } from "../../api";
import useCreateTicket from "../../contexts/CreateTicketContext";
import useLogging from "../../contexts/logging/LoggingContext";

const CREATE_REQUEST_ID = "create-agent-request";
const CLEAR_REQUEST_ID = "clear";

const NEXT_STEPS = [
    {
        id: "tickets",
        to: Paths.AGENT,
        params: Object.create(null),
        icon: <FaInbox />,
        title: "Inbox",
        shouldShowAlways: true,
        showPermission: Permission.ChatsModifyAll,
        hidePermission: undefined,
        description: "View the main ticket inbox.",
    },
    {
        id: "my-tickets",
        to: Paths.TICKETS,
        params: Object.create(null),
        icon: <BsList />,
        title: "My Tickets",
        shouldShowAlways: true,
        showPermission: Permission.TicketsReadSelf,
        hidePermission: Permission.ChatsModifyAll,
        description: "View my tickets.",

    },
    {
        id: "create-ticket",
        to: Paths.TICKETS_CREATE,
        params: Object.create(null),
        icon: <AiOutlinePlus />,
        title: "Create a Ticket",
        shouldShowAlways: true,
        showPermission: Permission.TicketCreateGuest,
        hidePermission: Permission.ChatsModifyAll,
        description: "Create a new request or ticket.",

    },

    {
        id: CREATE_REQUEST_ID,
        to: Paths.AGENT_CHAT,
        params: { chatId: "new" },
        icon: <AiOutlinePlus />,
        title: "Create a Ticket",
        shouldShowAlways: true,
        showPermission: Permission.ChatsModifyAll,
        description: "Create a new request or ticket.",

    },

    {
        id: "agent-compose",
        to: Paths.AGENT_COMPOSE,
        params: Object.create(null),
        icon: <BsFillMegaphoneFill />,
        title: "Compose a Message",
        shouldShowAlways: true,
        showPermission: Permission.ChatsModifyAll,
        description: "Compose a message to send to a one or many channels.",

    },
    {
        id: "knowledge",
        to: Paths.ANSWERS_LIST,
        params: Object.create(null),
        icon: <BiBookHeart />,
        title: "Answers",
        shouldShowAlways: true,
        showPermission: Permission.ChatsModifyAll,
        hidePermission: Permission.Administrator,
        description: "Create or view knowledge base articles.",

    },
    {
        id: "contacts",
        to: Paths.CONTACTS,
        params: Object.create(null),
        icon: <FaUsers />,
        title: "Contacts",
        shouldShowAlways: true,
        showPermission: Permission.ChatsModifyAll,
        description: "View all contacts and organizations.",

    },

    {
        id: "users",
        to: Paths.ADMINISTRATOR_SETTING,
        params: { setting: "users" },
        icon: <FaUsers />,
        title: "Set up Users",
        shouldShowAlways: false,
        showPermission: Permission.Administrator,
        description: "Add more agents or administrators.",

    },
    {
        id: "time",
        to: Paths.ADMINISTRATOR_SETTING,
        params: { setting: "time" },
        icon: <IoTimeOutline />,
        title: "Set up off-hours",
        shouldShowAlways: false,
        showPermission: Permission.Administrator,
        description: "Set up off-hours and display a message when you are away.",

    },
    {
        id: "feedback",
        to: Paths.ADMINISTRATOR_SETTING,
        params: { setting: "feedback" },
        icon: <BsHandThumbsUp />,
        title: "Set up Feedback",
        shouldShowAlways: false,
        showPermission: Permission.Administrator,
        description: "Set the integrated Feedback.",

    },
    {
        id: "queues",
        to: Paths.ADMINISTRATOR_SETTING,
        params: { setting: "queues" },
        icon: <BsShuffle />,
        title: "Set up Queues",
        shouldShowAlways: false,
        showPermission: Permission.Administrator,
        description: "Configure queues which manage ticket routing.",

    },
    {
        id: "services",
        to: Paths.ADMINISTRATOR_SETTING,
        params: { setting: "workflow-services" },
        icon: <BsShopWindow />,
        title: "Integrations",
        shouldShowAlways: false,
        showPermission: Permission.Administrator,
        description: "Configure integrations with your standard tools.",
    },
    // {
    //     id: CLEAR_REQUEST_ID,
    //     to: Paths.ADMINISTRATOR,
    //     params: Object.create(null),
    //     icon: <BsGearFill />,
    //     title: "Clear",
    //     showPermission: Permission.Administrator,
    //     description: "View and manage all settings",
    // },
    {
        id: "settings",
        to: Paths.ADMINISTRATOR_SETTING,
        params: { setting: "home" },
        icon: <BsGearFill />,
        title: "Settings",
        shouldShowAlways: true,
        showPermission: Permission.Administrator,
        description: "View and manage all settings",
    },

    {
        id: "my-profile",
        to: Paths.ACCOUNT_PROFLE,
        params: Object.create(null),
        icon: <IoPerson />,
        title: "My Profile",
        shouldShowAlways: true,
        showPermission: Permission.ChatsModifyAll,
        description: "View my profile and personal settings.",
    },

];
export default function Home(): ReactElement {
    const { getSearchParams, historyPushWithSearch } = useRouting();
    const logging = useLogging();
    const { permissions } = useCurrentSession();
    const { data: onboardingData } = useGetOnboardingProfileQuery({
        skip: !permissions.has(Permission.Administrator),
    });
    const [saveProfile] = useSaveOnboardingProfileMutation();
    const searchParams = getSearchParams(Paths.HOME);
    const { isSuccess, isOnboarded } = searchParams;
    const createTicket = useCreateTicket();

    useEffect(() => {
        if (isSuccess) {
            setTimeout(() => {
                delete (searchParams as Record<string, string>).isSuccess;
                historyPushWithSearch(Paths.HOME, searchParams);
            }, 1000);
        }
    }, [isSuccess]);

    const homeStepsCompleted = new Set(onboardingData?.onboardingProfile?.homeStepsCompleted ?? []);
    const steps = NEXT_STEPS.filter(step => permissions.has(step.showPermission)
            && (!step.hidePermission || !permissions.has(step.hidePermission))
            && (step.shouldShowAlways || !homeStepsCompleted.has(step.id)))
        .limit(6);

    const saveStepVisited = (event: MouseEvent, step: typeof NEXT_STEPS[0]) => {
        logging.debug({ step: step.id }, "User visited home step");
        if (step.id === CREATE_REQUEST_ID) {
            createTicket.show();
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (step.shouldShowAlways || !permissions.has(Permission.Administrator) || !onboardingData?.onboardingProfile) {
            return;
        }
        homeStepsCompleted.add(step.id);
        saveProfile({
            variables: {
                profile: removeTypename({
                    ...onboardingData?.onboardingProfile,
                    homeStepsCompleted: step.id === CLEAR_REQUEST_ID ? [] : Array.from(homeStepsCompleted),
                    isExternalSupport: undefined,
                }),
            },
        });
    };

    return (
        <div className="full-page">
            <div className="centered-container centered-container-lg pt-5 align-items-start">

                <h4 className="text-center w-100 mb-4">
                    {isOnboarded ? (
                        "🎉 Onboarding Complete! What do you want to do next?"
                    ) : (
                        "👋 Hey, What do you want to do today?"
                    )}
                </h4>
                {isSuccess && (
                    <>
                        <Confetti
                            width={document.body.clientWidth}
                            height={document.body.scrollHeight}
                            initialVelocityY={30}
                            initialVelocityX={20}
                            numberOfPieces={300}
                            recycle={false}
                            confettiSource={{
                                /* eslint-disable id-length */
                                x: 0,
                                y: document.body.scrollHeight,
                                w: 0,
                                h: 0,
                                /* eslint-enable id-length */
                            }}
                        />
                        <Confetti
                            width={document.body.clientWidth}
                            height={document.body.scrollHeight}
                            initialVelocityY={30}
                            initialVelocityX={-20}
                            numberOfPieces={300}
                            recycle={false}
                            confettiSource={{
                                /* eslint-disable id-length */
                                x: document.body.clientWidth,
                                y: document.body.scrollHeight,
                                w: 0,
                                h: 0,
                                /* eslint-enable id-length */
                            }}
                        />
                    </>
                )}

                <div className="list-group list-group-horizontal w-100 flex-wrap">
                    {steps.map(step => (
                        <div className="col-6 col-lg-4 p-2" key={step.id}>
                            <NavLink
                                to={step.to}
                                params={step.params}
                                className="list-group-item p-3 rounded hover-shadow mx-auto h-100"
                                onClick={evt => saveStepVisited(evt, step)}
                            >
                                <div className="mb-2">
                                    <SquareIcon className="me-2">
                                        {step.icon}
                                    </SquareIcon>
                                    <strong>{step.title}</strong>
                                </div>
                                <p>
                                    {step.description}
                                </p>
                            </NavLink>
                        </div>
                    ))}

                </div>
                <div className="text-end w-100">
                    <Help
                        articleId={isOnboarded ? ARTICLES.ONBOARDING_HOME_PAGE : ARTICLES.HOME_PAGE}
                        buttonClassName="btn btn-none text-light mx-auto"
                        button={(
                            <span>
                                <BiHelpCircle />
                                {" "}
                                Help
                            </span>
                        )}
                    />
                </div>

            </div>

        </div>
    );
}
